import { z } from 'zod';
import {
  AuditEntrySchema,
  type AuditEntry,
  type CardDetail,
  CardDetailSchema,
  CardSchema,
  ConversationBoardCardSchema,
  type CardEvent,
  type CardId,
  type CardColumnConfig,
  CardColumnConfigSchema,
  type ResolvedRetention,
  ResolvedRetentionSchema,
  type SquadId,
  TaskSchema,
  type TaskColumnConfig,
  TaskColumnConfigSchema,
  type TaskWithCard,
  TaskWithCardSchema,
} from '@voll-intelligence/domain';
import type { HttpService } from '@web/data/services';

const ConversationBoardResponseSchema = z.object({
  columns: z.object({
    backlog: z.array(ConversationBoardCardSchema),
    in_progress: z.array(ConversationBoardCardSchema),
    resolved: z.array(ConversationBoardCardSchema),
  }),
});

export type ConversationBoardResponse = z.infer<typeof ConversationBoardResponseSchema>;

export type { TaskWithCard } from '@voll-intelligence/domain';

const TaskBoardResponseSchema = z.object({
  columns: z.object({
    ai_handling: z.array(TaskWithCardSchema),
    awaiting_human: z.array(TaskWithCardSchema),
    human_handling: z.array(TaskWithCardSchema),
    resolved: z.array(TaskWithCardSchema),
  }),
});

export type TaskBoardResponse = z.infer<typeof TaskBoardResponseSchema>;

const CardEventOutcomeSchema = z.object({
  card: CardSchema.nullish(),
  task: TaskSchema.nullish(),
  auditEntryId: z.string(),
});

type CardEventOutcome = z.infer<typeof CardEventOutcomeSchema>;

const CardDetailResponseSchema = z.object({ card: CardDetailSchema });

const AuditListResponseSchema = z.object({ entries: z.array(AuditEntrySchema) });

const MyTasksResponseSchema = z.object({ tasks: z.array(TaskWithCardSchema) });

interface AuditFilters {
  actorKind?: 'agent' | 'operator';
  action?: string;
}

/**
 * Wire shape for the per-org board config. `_v` is the optimistic-
 * locking counter — read on GET, echoed back as `expectedV` on PUT.
 */
const BoardConfigResponseSchema = z.object({
  _v: z.number().int().min(0),
  cardColumns: z.array(CardColumnConfigSchema),
  taskColumns: z.array(TaskColumnConfigSchema),
  resolvedRetention: ResolvedRetentionSchema,
  updatedAt: z.coerce.date(),
  updatedByUserId: z.string(),
});

export type BoardConfigResponse = z.infer<typeof BoardConfigResponseSchema>;

export interface UpdateBoardConfigInput {
  expectedV: number;
  cardColumns: CardColumnConfig[];
  taskColumns: TaskColumnConfig[];
  resolvedRetention: ResolvedRetention;
}

interface BoardRepository {
  loadConversationBoard: (orgId: string) => Promise<ConversationBoardResponse>;
  loadTaskBoard: (orgId: string) => Promise<TaskBoardResponse>;
  /**
   * Squad-scoped board endpoints. Same response shape as the org-wide
   * endpoints, filtered server-side to the given squad. Backs the
   * `/squad/$squadId` board route.
   */
  loadSquadConversationBoard: (
    orgId: string,
    squadId: SquadId,
  ) => Promise<ConversationBoardResponse>;
  loadSquadTaskBoard: (orgId: string, squadId: SquadId) => Promise<TaskBoardResponse>;
  /**
   * Flat list of the viewing user's squad tasks — backs "Minhas tarefas".
   * The agent is resolved server-side from the auth token; 'done' tasks are
   * always excluded.
   */
  loadMyTasks: (orgId: string, squadId: SquadId) => Promise<TaskWithCard[]>;
  /**
   * Tasks attached to a single card — backs the drawer Tasks tab.
   * Dedicated endpoint so the drawer doesn't pull the whole squad board.
   */
  loadCardTasks: (orgId: string, cardId: CardId) => Promise<TaskWithCard[]>;
  /**
   * Read-side card detail. Backs the drawer header (customer / company /
   * channel) and the identity tab. `CardDetail` is the persisted card shape.
   */
  loadCardDetail: (orgId: string, cardId: CardId) => Promise<CardDetail>;
  /**
   * The card of a conversation, whole (the traveler view). Unlike
   * `loadCardDetail` it is open to every org member, guests included.
   * 404 → HttpError(404): no card.
   */
  findCardByConversation: (orgId: string, conversationId: string) => Promise<CardDetail>;
  loadCardAudit: (orgId: string, cardId: CardId, filters?: AuditFilters) => Promise<AuditEntry[]>;
  dispatchCardEvent: (
    orgId: string,
    cardId: CardId,
    payload: CardEvent,
  ) => Promise<CardEventOutcome>;
  loadBoardConfig: (orgId: string) => Promise<BoardConfigResponse>;
  updateBoardConfig: (
    orgId: string,
    payload: UpdateBoardConfigInput,
  ) => Promise<BoardConfigResponse>;
}

function orgPath(orgId: string, suffix: string): string {
  return `/orgs/${encodeURIComponent(orgId)}${suffix}`;
}

function squadPath(orgId: string, squadId: SquadId, suffix: string): string {
  return `/orgs/${encodeURIComponent(orgId)}/squads/${encodeURIComponent(squadId)}${suffix}`;
}

function cardPath(orgId: string, cardId: CardId, suffix = ''): string {
  return `/orgs/${encodeURIComponent(orgId)}/cards/${encodeURIComponent(cardId)}${suffix}`;
}

export function createBoardRepository(http: HttpService): BoardRepository {
  return {
    loadConversationBoard: (orgId) =>
      http.get(orgPath(orgId, '/boards/conversation'), {
        schema: ConversationBoardResponseSchema,
      }),
    loadTaskBoard: (orgId) =>
      http.get(orgPath(orgId, '/boards/tasks'), {
        schema: TaskBoardResponseSchema,
      }),
    loadSquadConversationBoard: (orgId, squadId) =>
      http.get(squadPath(orgId, squadId, '/boards/conversation'), {
        schema: ConversationBoardResponseSchema,
      }),
    loadSquadTaskBoard: (orgId, squadId) =>
      http.get(squadPath(orgId, squadId, '/boards/tasks'), {
        schema: TaskBoardResponseSchema,
      }),
    loadMyTasks: async (orgId, squadId) => {
      const out = await http.get(squadPath(orgId, squadId, '/my-tasks'), {
        schema: MyTasksResponseSchema,
      });
      return out.tasks;
    },
    loadCardTasks: async (orgId, cardId) => {
      const out = await http.get(cardPath(orgId, cardId, '/tasks'), {
        schema: MyTasksResponseSchema,
      });
      return out.tasks;
    },
    loadCardDetail: async (orgId, cardId) => {
      const out = await http.get(cardPath(orgId, cardId), {
        schema: CardDetailResponseSchema,
      });
      return out.card;
    },
    findCardByConversation: async (orgId, conversationId) => {
      const out = await http.get(
        `/orgs/${encodeURIComponent(orgId)}/conversations/${encodeURIComponent(conversationId)}/card`,
        { schema: CardDetailResponseSchema },
      );
      return out.card;
    },
    loadCardAudit: async (orgId, cardId, filters) => {
      const query: Record<string, string | undefined> = {};
      if (filters?.actorKind) query.actorKind = filters.actorKind;
      if (filters?.action) query.action = filters.action;
      const out = await http.get(cardPath(orgId, cardId, '/audit'), {
        schema: AuditListResponseSchema,
        query,
      });
      return out.entries;
    },
    dispatchCardEvent: (orgId, cardId, payload) =>
      http.post(cardPath(orgId, cardId, '/events'), payload, {
        schema: CardEventOutcomeSchema,
      }),
    loadBoardConfig: (orgId) =>
      http.get(orgPath(orgId, '/board-config'), {
        schema: BoardConfigResponseSchema,
      }),
    updateBoardConfig: (orgId, payload) =>
      http.put(orgPath(orgId, '/board-config'), payload, {
        schema: BoardConfigResponseSchema,
      }),
  };
}
